import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Pagination } from 'common/components';
import { useGetCallsData, useGetLiveMonitorStats, useGetPartyStages } from 'services/hooks/queries';
import { CallType, type SortDirection, type SortField } from 'types/call.type';
import type { AgenticWorkflowOutletContext, Lead } from '../AgenticWorkflowDetail.types';
import { formatDuration, formatDurationTooltip, setDateTimes } from '../utils';
import {
    EMPTY_LIVE_MONITOR_STATS,
    LiveMonitorDrawer,
    LiveMonitorFilters,
    LiveMonitorStats,
    LiveMonitorTable,
    LIVE_MONITOR_ACTIVE_STATUS_FILTER,
    type LiveMonitorFilterName,
    type LiveMonitorStatMetrics,
} from '../components/LiveMonitor';
import { QueueDelayAlertBanner } from '../components/QueueDelayAlertBanner';

import styles from './LiveMonitorPage.module.scss';

const LiveMonitorPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { initialStartDate, initialEndDate, isWorkflowActive } = useOutletContext<AgenticWorkflowOutletContext>();

    const [filterLeadStatus, setFilterLeadStatus] = useState('');
    const [filterCallStatus, setFilterCallStatus] = useState('');
    const [filterCallDirection, setFilterCallDirection] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedCall, setSelectedCall] = useState<Lead | null>(null);
    const [sortField] = useState<SortField>('callDate');
    const [sortDirection] = useState<SortDirection>('desc');

    const { startWithTime, endWithTime } = setDateTimes([initialStartDate, initialEndDate]);

    const effectiveCallStatus = filterCallStatus || LIVE_MONITOR_ACTIVE_STATUS_FILTER;

    const sharedFilters = useMemo(
        () => ({
            startDate: startWithTime,
            endDate: endWithTime,
            callStatus: effectiveCallStatus,
            partyStage: filterLeadStatus,
            callDirection: filterCallDirection,
            search: '',
        }),
        [startWithTime, endWithTime, effectiveCallStatus, filterLeadStatus, filterCallDirection]
    );

    const callsQueryParams = useMemo(
        () => ({
            id: id || '',
            startDate: sharedFilters.startDate,
            endDate: sharedFilters.endDate,
            callStatus: sharedFilters.callStatus,
            partyStage: sharedFilters.partyStage,
            callDirection: sharedFilters.callDirection,
            search: sharedFilters.search,
            perPage: rowsPerPage,
            sortOrder: sortDirection,
            sortBy: sortField,
            currentPage,
            combinedOverdue: true,
        }),
        [id, sharedFilters, rowsPerPage, sortDirection, sortField, currentPage]
    );

    const statsQueryParams = useMemo(
        () => ({
            id: id || '',
            callDirection: filterCallDirection || undefined,
        }),
        [id, filterCallDirection]
    );

    const { liveMonitorStats } = useGetLiveMonitorStats(statsQueryParams, Boolean(id) && Boolean(isWorkflowActive));
    const { partyStages } = useGetPartyStages(Boolean(isWorkflowActive));
    const { callsData, isCallsDataLoading } = useGetCallsData(
        callsQueryParams,
        Boolean(id) && Boolean(isWorkflowActive),
        Boolean(isWorkflowActive)
    );

    const metrics = useMemo<LiveMonitorStatMetrics>(() => {
        if (!liveMonitorStats) return EMPTY_LIVE_MONITOR_STATS;
        return {
            activeCalls: liveMonitorStats.activeCalls,
            queued: liveMonitorStats.queued,
            pending: liveMonitorStats.pending,
            avgDurationSeconds: liveMonitorStats.avgDurationSeconds,
        };
    }, [liveMonitorStats]);

    const leads = useMemo<Lead[]>(() => {
        if (!callsData?.data?.length) return [];
        return callsData.data.map((call) => {
            const callDateTime = new Date(call.startTime || call.scheduledDate);
            const durationSeconds = call.callDuration || 0;
            return {
                id: call.crmId,
                callId: call.callId,
                callDate: callDateTime.toLocaleDateString('en-US'),
                callTime: callDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                outcome: call.status || CallType.PENDING,
                duration: formatDuration(durationSeconds),
                durationTooltip: formatDurationTooltip(durationSeconds),
                crmStatus: call.partyStage || 'NEW_LEAD',
                crmId: call.crmId,
                name: call.contactName || 'Unknown',
                number: call.phoneNumber || 'Unknown',
                transferTo: call.transferTo || '',
                disposition: call.disposition || '',
                callDirection: call.callDirection,
                action: '',
                recording: call.callId,
                transcript: call.callId,
                aggregatedTagsUsed: call.aggregatedTagsUsed,
            };
        });
    }, [callsData]);

    const totalPages = callsData?.pagination?.totalPages || 1;
    const currentPageFromAPI = callsData?.pagination?.currentPage || currentPage;
    const perPageFromAPI = callsData?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        const paginationData = callsData?.pagination;
        if (paginationData?.currentPage) setCurrentPage(paginationData.currentPage);
        if (paginationData?.perPage) setRowsPerPage(paginationData.perPage);
    }, [callsData?.pagination]);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    useEffect(() => {
        if (!selectedCall) return;
        const stillVisible = leads.some((lead) => lead.callId === selectedCall.callId);
        if (!stillVisible) setSelectedCall(null);
    }, [leads, selectedCall]);

    const handleFilterChange = (filter: LiveMonitorFilterName, value: string) => {
        if (filter === 'leadStatus') setFilterLeadStatus(value);
        else if (filter === 'callStatus') setFilterCallStatus(value);
        else setFilterCallDirection(value);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilterLeadStatus('');
        setFilterCallStatus('');
        setFilterCallDirection('');
        setCurrentPage(1);
    };

    if (!isWorkflowActive) {
        return (
            <div className={styles.pausedState} data-testid='live-monitor-paused'>
                <h3 className={styles.pausedTitle}>Live Monitor unavailable</h3>
                <p className={styles.pausedCopy}>
                    Start the workflow to monitor active calls, queue depth, and live transcripts.
                </p>
                <button type='button' className={styles.pausedLink} onClick={() => navigate(`/agentic-workflow/${id}`)}>
                    Back to Call Analytics
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page} data-testid='live-monitor-page'>
            <QueueDelayAlertBanner alert={liveMonitorStats?.queueDelayAlert} />

            <LiveMonitorStats metrics={metrics} />

            <div className={styles.mainLayout}>
                <div className={styles.tableColumn}>
                    <div className={styles.tableHeader}>
                        <h3 className={styles.sectionTitle}>Active calls</h3>
                        <p className={styles.sectionSubtitle}>
                            Queued, pending, ringing, and in-progress calls only. No search.
                        </p>
                    </div>

                    <LiveMonitorFilters
                        filterCallStatus={filterCallStatus}
                        filterLeadStatus={filterLeadStatus}
                        filterCallDirection={filterCallDirection}
                        partyStages={partyStages?.data ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={handleClearFilters}
                    />

                    <LiveMonitorTable
                        leads={leads}
                        selectedCall={selectedCall}
                        isLoading={isCallsDataLoading}
                        onSelectCall={setSelectedCall}
                    />

                    <Pagination
                        currentPage={currentPageFromAPI}
                        totalPages={totalPages}
                        rowsPerPage={perPageFromAPI}
                        onPageChange={setCurrentPage}
                        onRowsPerPageChange={(next) => {
                            setRowsPerPage(next);
                            setCurrentPage(1);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        className={styles.pagination}
                    />
                </div>

                <div className={styles.drawerColumn}>
                    <LiveMonitorDrawer selectedCall={selectedCall} onClose={() => setSelectedCall(null)} />
                </div>
            </div>
        </div>
    );
};

export default LiveMonitorPage;
